import { ResponsiveCell } from '../shared/ResponsiveTable';
import { Pill, Photo } from './ClientHybridCard';
import { dealSummary, hasHumanReview, money, statusMeta, tierTone, toneTextStyle } from './clientsConstants';

// Module 2 — Tier 1: the collapsed line (UX Addendum §3).
//
// The WHOLE LINE is the target — there is no per-row "show more" control. Clicking it
// cascades open, in place, into the Tier-2 Hybrid card. That is the platform's one
// interaction grammar: learn it here, meet it again on bookings, invoices and dispatch
// (Pattern Library entry #1, `disclosure.inline-edit`).
//
// A row is an ACCOUNT (Val, 2026-07-15: "everyone is an account, with a primary contact"),
// wearing its primary contact's face.
//
// `detail` is the card to reveal (Tier 2 or Tier 3) — the row doesn't decide which, it just
// makes room for it. That keeps the disclosure grammar here and the card choice in one place.
//
// No `dark` prop: colour is named by role and swapped by the .dark class in CSS.

function AccountRow({ account, isOpen, onToggle, onCollapseAll, colSpan, detail }) {
    const contact = account.primary_contact;
    const meta = statusMeta(account.status);
    const deal = dealSummary(account);

    return (
        <>
            <tr
                onClick={() => onToggle(account.id)}
                className={`cursor-pointer transition hover:bg-[var(--surface-sunken)]
                            ${isOpen ? 'bg-[var(--surface-sunken)]' : ''}`}
                aria-expanded={isOpen}
            >
                <ResponsiveCell label="Contact" className="px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <Photo contact={contact} size="sm" />
                        <span className="font-medium truncate text-[var(--ink)]">
                            {contact?.name || <span className="italic text-[var(--ink-muted)]">No contact</span>}
                        </span>
                    </div>
                </ResponsiveCell>
                <ResponsiveCell label="Position" className="px-4 py-3 text-sm truncate" style={toneTextStyle('system')}>{contact?.position || '—'}</ResponsiveCell>
                <ResponsiveCell label="Company" className="px-4 py-3 text-sm truncate text-[var(--ink)]">{account.account_name}</ResponsiveCell>
                <ResponsiveCell label="Account #" className="px-4 py-3 text-xs font-mono text-[var(--ink-muted)]">
                    {account.account_number || '—'}
                </ResponsiveCell>
                <ResponsiveCell label="Tier" className="px-4 py-3">
                    {account.tier
                        ? <Pill tone={tierTone(account.tier.family)}>{account.tier.name}</Pill>
                        : <span className="text-xs text-[var(--ink-muted)]">—</span>}
                </ResponsiveCell>
                <ResponsiveCell label="Status" className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Pill tone={meta.tone} title={meta.hint}>{meta.label}</Pill>
                        {hasHumanReview(account) && (
                            <Pill tone="open" title="Requires human review before booking">Needs review</Pill>
                        )}
                    </div>
                </ResponsiveCell>
                <ResponsiveCell label="Pipeline" className="px-4 py-3">
                    {deal ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            {deal.stage && <Pill tone="system">{deal.stage.name}</Pill>}
                            <span className="text-sm font-semibold text-[var(--ink)]">{money(deal.total)}</span>
                        </div>
                    ) : <span className="text-xs text-[var(--ink-muted)]">—</span>}
                </ResponsiveCell>
            </tr>

            {/* The reveal opens in place, inside the table — not a modal, not a new page. */}
            {isOpen && (
                <tr className="bg-[var(--surface-canvas)]">
                    <td
                        colSpan={colSpan}
                        className="px-4 pb-4 pt-1"
                        onDoubleClick={() => onCollapseAll?.()}
                    >
                        {detail}
                    </td>
                </tr>
            )}
        </>
    );
}

export default AccountRow;
